import { Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ChipProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

type ClayColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default';
type ClayVariant = 'filled' | 'outlined';

export interface ClayChipProps extends Omit<ChipProps, 'color' | 'variant'> {
  color?: ClayColor;
  variant?: ClayVariant;
}

function getClayStyles(color: ClayColor, variant: ClayVariant, isDark: boolean, theme: Theme) {
  // ── Default (neutral grey) ──────────────────────────────────────────────
  if (color === 'default') {
    if (variant === 'outlined') {
      return {
        backgroundColor: 'transparent',
        border: `1.5px solid ${alpha(theme.palette.divider, 0.8)}`,
        color: theme.palette.text.secondary,
        '& .MuiChip-icon': { color: theme.palette.text.secondary },
      };
    }
    return {
      background: isDark
        ? `linear-gradient(145deg, ${theme.palette.grey[700]}, ${theme.palette.grey[800]})`
        : `linear-gradient(145deg, ${theme.palette.grey[100]}, ${theme.palette.grey[200]})`,
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      color: theme.palette.text.secondary,
      boxShadow: [
        `0 2px 6px rgba(0,0,0,${isDark ? 0.25 : 0.08})`,
        `inset 0 1px 0 ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)'}`,
        `inset 0 -1px 0 rgba(0,0,0,${isDark ? 0.18 : 0.05})`,
      ].join(', '),
      '& .MuiChip-icon': { color: theme.palette.text.secondary },
    };
  }

  // ── Semantic color ──────────────────────────────────────────────────────
  const p = theme.palette[color];

  if (variant === 'outlined') {
    return {
      backgroundColor: alpha(p.main, isDark ? 0.1 : 0.06),
      border: `1.5px solid ${alpha(p.main, isDark ? 0.45 : 0.35)}`,
      color: isDark ? p.light : p.dark,
      boxShadow: [
        `0 2px 8px ${alpha(p.main, isDark ? 0.2 : 0.1)}`,
        `inset 0 1px 0 ${alpha(theme.palette.common.white, isDark ? 0.06 : 0.7)}`,
      ].join(', '),
      '& .MuiChip-icon': { color: isDark ? p.light : p.main },
      '& .MuiChip-label': { fontWeight: 600 },
    };
  }

  // filled
  return {
    background: `linear-gradient(145deg, ${p.light}, ${p.main})`,
    border: `1px solid ${alpha(p.dark, 0.2)}`,
    color: p.contrastText,
    boxShadow: [
      `0 3px 10px ${alpha(p.main, isDark ? 0.52 : 0.36)}`,
      `0 1px 3px rgba(0,0,0,0.1)`,
      `inset 0 1px 0 rgba(255,255,255,0.28)`,
      `inset 0 -1px 0 ${alpha(p.dark, 0.25)}`,
    ].join(', '),
    '& .MuiChip-icon': { color: alpha(p.contrastText, 0.9) },
    '& .MuiChip-label': { fontWeight: 600 },
  };
}

export function ClayChip({
  color = 'default',
  variant = 'filled',
  sx,
  ...props
}: ClayChipProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const clayStyles = getClayStyles(color, variant, isDark, theme);

  return (
    <Chip
      {...props}
      sx={{
        borderRadius: 2,
        ...clayStyles,
        ...(sx as object),
      }}
    />
  );
}
